/**
 * StreetcarBody — visible No. 39 streetcar body rendered as a scene child
 * and driven along the ride spline from the parent StreetcarRig.
 *
 * Plan 08 chase-cam pass. Extracted from StreetcarRig.tsx on 2026-04-24
 * because the chase-camera refactor flips the role of the .glb: previously
 * it was a cosmetic body you could not see (first-person camera sat
 * INSIDE it); now it is the primary visible vehicle the camera chases from
 * behind. Splitting into its own component makes the rig file focused on
 * camera pose only and lets this body be unit-tested in isolation.
 *
 * Scene-graph cloning (important):
 *   The streetcar-no39.glb is ALSO referenced by a static manifest entry
 *   (`streetcar-no39` in world.json, position [0, 2, -3]) that renders via
 *   the SceneGraph. drei's `useGLTF` caches the loaded gltf by URL, so
 *   both the static instance and this runtime rig receive the SAME gltf
 *   object. A THREE.Object3D can only have one parent, and any mutation
 *   to `gltf.scene` (position, rotation) leaks to both consumers — the
 *   static one visibly warps when the rig tweaks positions. We clone the
 *   scene once on mount so the rig has its own Object3D tree that can be
 *   freely repositioned without leaking to the static instance.
 *
 * Authored translation:
 *   The streetcar-no39.glb scene-root is authored with
 *   LAYOUT.birmingham.streetcarStaticOrigin = [0, 2, -3] so a standalone
 *   render of the .glb sits the car "parked at the Birmingham platform."
 *   At runtime we want the body anchored at (0, 0, 0) of its local frame
 *   so that setting bodyRef.position to a spline point places the car
 *   AT that point — not at (spline + authored_origin). We neutralize
 *   the authored translation on the CLONED scene so the runtime rig is
 *   self-contained.
 *
 * The streetcar authored floor (wheels-at-ground) is at local y=0. At runtime
 * we translate the cloned scene DOWN by `BODY_FLOOR_LIFT` so the wheels sit
 * slightly BELOW the spline point — the spline threads through roughly the
 * body's mid-height (y≈2 at waypoint[0]) so the car's clerestory roofline
 * tops out near the original eye-height.
 */
import { Component, Suspense, useMemo } from 'react';
import type { ErrorInfo, ReactNode, MutableRefObject } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';

/**
 * Downward offset applied to the streetcar body relative to the spline
 * point that drives it. The spline waypoints put the ride path at y≈2m at
 * the Birmingham platform and ~y≈22m over the crest of Red Mountain — these
 * values were chosen to match human eye-height in first-person POV. In
 * chase-cam mode we keep the same spline, but we want the wheels of the
 * streetcar to sit roughly `BODY_FLOOR_LIFT` below the spline point so the
 * clerestory roofline tops out close to the original eye-height. 1.4m is
 * chosen so wheels sit around spline_y - 1.4m (about ground-plane at the
 * Birmingham platform) and roof tops out around spline_y + 1.4m.
 */
export const BODY_FLOOR_LIFT = 1.4;

/**
 * Error boundary that swallows streetcar-body .glb load failures. Without
 * this, a failed fetch inside <Suspense> propagates as an unhandled error
 * and would crash the ride camera. Kept identical to the pre-refactor
 * behaviour so tests that rely on "rig mounts without throwing when .glb
 * fails to fetch" still pass.
 */
class StreetcarBodyErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(): { hasError: true } {
    return { hasError: true };
  }
  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.warn(
      '[StreetcarBody] streetcar body .glb failed to load; ride continues without visible body.',
    );
  }
  render(): ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

/**
 * Inner component: loads the .glb, neutralizes its authored translation,
 * and exposes a group ref the parent drives every frame.
 */
function StreetcarBodyInner({
  bodyRef,
}: {
  bodyRef: MutableRefObject<THREE.Group | null>;
}) {
  const gltf = useGLTF('/models/shared/streetcar/no39.glb');

  // Clone the loaded scene so we can mutate its transform without affecting
  // the static manifest instance (which shares the same useGLTF cache entry).
  // Memoized on the gltf.scene reference so re-renders don't spawn new clones.
  const clonedScene = useMemo(() => {
    if (!gltf?.scene) return null;
    const clone = gltf.scene.clone(true);
    // Neutralize the authored `streetcarStaticOrigin = [0, 2, -3]` translation
    // AND apply the BODY_FLOOR_LIFT offset in a single position write. The
    // clone's root node inherits the authored position from the gltf scene
    // graph; overwriting it here places the body at local-origin - floorlift.
    clone.position.set(0, -BODY_FLOOR_LIFT, 0);
    return clone;
  }, [gltf?.scene]);

  if (!clonedScene) return null;
  return (
    <group ref={bodyRef}>
      <primitive object={clonedScene} />
    </group>
  );
}

/**
 * Public wrapper — Suspense + ErrorBoundary so the ride camera keeps
 * running if the .glb hasn't streamed in yet or fails to load.
 */
export function StreetcarBody({
  bodyRef,
}: {
  bodyRef: MutableRefObject<THREE.Group | null>;
}) {
  return (
    <StreetcarBodyErrorBoundary>
      <Suspense fallback={null}>
        <StreetcarBodyInner bodyRef={bodyRef} />
      </Suspense>
    </StreetcarBodyErrorBoundary>
  );
}

/**
 * Hook-style helper that composes the per-frame transform for the body
 * given a spline point, a look-ahead point, and a ref to the group.
 * Extracted so StreetcarRig can call it without duplicating the math and
 * so tests can invoke it directly.
 *
 * We build an orientation quaternion from the forward direction
 * (lookAhead - pos) and the world-up vector — NOT from the camera
 * quaternion, which in chase-cam mode points DOWN-FORWARD rather than
 * FORWARD.
 */
export function applyStreetcarBodyPose(
  group: THREE.Group | null,
  pos: THREE.Vector3,
  lookAhead: THREE.Vector3,
) {
  if (!group) return;
  group.position.copy(pos);
  // Orientation: face along (lookAhead - pos) with world up. lookAt on
  // a Group rotates so local -Z points at the target. The authored
  // streetcar .glb has the carbody long axis along X (BODY_LEN spans the
  // X axis in streetcar-no39.mjs), so after lookAt we apply a -90° Y
  // rotation so the long axis aligns with the forward direction (-Z of
  // the group's local frame post-lookAt).
  group.lookAt(lookAhead);
  // Streetcar long axis is +X in authored space; lookAt aims -Z at
  // target, so rotate the group so its +X now points at the target.
  group.rotateY(-Math.PI / 2);
}

// Preload so the asset is ready the instant the ride starts.
useGLTF.preload('/models/shared/streetcar/no39.glb');
